
import { Calendar } from "@/models/calendar";
import { CalendarEvent } from "@/models/calendar-event";
import { Calendar as FullCalendarApi, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import AppFullCalendar from "@fullcalendar/vue";
import AppCalendarEventEdit from "./CalendarEventEditCard.vue";
import AppEventTypeSelectModal from "./EventTypeSelectModal.vue";
import AppGoogleCalendarEventEdit from "./GoogleCalendarEventEditCard.vue";
import AppTaskView from "./TaskView.vue";

import { CalendarService } from "@/services/calendar-service";
import { GoogleCalendarService } from "@/services/google-calendar-service";
import { UserConfigService } from "@/services";
import { CalendarState, CalendarViewType, IOnDropProps } from "@/utils/calendar-state";
import { EventSourceInput } from "@fullcalendar/core/structs/event-source";
import Vue from "vue";
import { TaskService } from "../../services/task-service";
import { Folder } from "../../models/tasks/folder";
import { Task } from "../../models/tasks/task";
import AppTaskEdit from "./TaskEdit.vue";
import { FolderUser } from "../../models/tasks/folder-user";
import AppCalendarControls from "./CalendarControls.vue";
import AppCalendarDayPerCalendarView from "./CalendarDayPerCalendarView.vue";


export default Vue.extend({
	data() {
		return {
			toEditEvent: null as null | CalendarEvent,
			toEditGoogleEvent: null as null | gapi.client.calendar.Event,
			toEditTask: null as null | Task,
			toEditGoogleEventCalendarId: "",
			toViewTask: null as null | Task,
			toViewTaskFolder: null as null | FolderUser,
			newEventModal: false,
			clickedDate: new Date(),
			isDragging: false,
			dragInited: false,
			preventSwipe: false,
			dragBeginPosition: {
				x: 0,
				y: 0
			},
		};
	},
	props: {
		state: {
			type: Object as () => CalendarState,
			required: true
		},
		event: {
			type: Object as () => CalendarEvent,
			required: false
		}
	},
	computed: {
		calendars(): Calendar[] {
			return this.state.selectedCalendars;
		},
		views(): any {
			let obj = this;
			return {
				timeGridWeek: {
					type: "timeGridWeek",
					columnHeaderHtml(date: Date) {
						let weekDay = obj.$t(`common.weekday.${date.getUTCDay()}`).toString();
						if (obj.$wf.isMdDown) {
							weekDay = weekDay.substring(0, 2);
						}
						return `${weekDay}<br/>${date.getDate()}`;
					},
					allDayText: "",
				},
				timeGridDay: {
					type: "timeGridDay",
					columnHeaderText(date: Date) {
						let weekDay = obj.$t(`common.weekday.${date.getUTCDay()}`).toString();
						return `${weekDay} (${date.getDate()}/${date.getUTCMonth() + 1})`;
					},
					allDayText: ""
				},
			};
		},
		timeFormat(): any {
			return {
				hour: "numeric",
				minute: "numeric",
				meridiem: false,
				hour12: false
			};
		},
		googleCalendars(): gapi.client.calendar.CalendarListEntry[] {
			return this.state.selectedGoogleCalendars;
		},
		calendarPlugins(): any[] {
			return [ // plugins must be defined in the JS
				dayGridPlugin,
				timeGridPlugin,
				interactionPlugin // needed for dateClick
			];
		},
		calendarEventSources(): EventSourceInput[] {
			let items = [
				...(this.googleCalendars.map(c => {
					return GoogleCalendarService.createFullCalendarEventSource(c);
				})),
				CalendarService.createEventSource(this.calendars),
				TaskService.createFullCalendarEventSource(this.state.selectedTaskFolders.map(f=>f.Folder as Folder))
			] as EventSourceInput[];
			return items;
		},
		isDayPerCalendarView():boolean {
			return this.state.currentViewType == CalendarViewType.DayPerCalendar;
		},
		firstDay():number {
			return UserConfigService.getCalendarSettings().FirstDayOfWeek;
		}
	},
	watch: {
		isDayPerCalendarView: {
			immediate: true,
			handler(){
				setTimeout(()=>{
					if (this.$refs.fullCalendar){
						this.state.setCalendarApis([(this.$refs.fullCalendar as any).getApi() as FullCalendarApi]);
					}
					this.onResize();
				}, 100);
			}
		},
	},
	methods: {
		refresh() {
			this.state.refetchEvents();
			this.$emit("success");
		},
		handleDateClick(event: { date: Date, jsEvent: MouseEvent }) {
			if (this.isDragging) return;
			if (this.state.calendars.length == 0 && this.state.googleCalendars.length == 0) return;
			this.clickedDate = event.date;
			if (this.googleCalendars.length == 0) {
				return this.onGotEventType("local");
			}
			if (this.calendars.length == 0) {
				return this.onGotEventType("google");
			}
			this.newEventModal = true;
		},
		async handleEventClick(e: { event: EventInput }) {
			if (this.isDragging) return;
			if (!e.event.id) {
				return;
			}
			let props = e.event.extendedProps as any;
			if (props.isTask){
				let task = await TaskService.getTask(props.id);
				let folder = await TaskService.getFolder(task.FolderID);
				if (folder.IsOwner){
					this.toEditTask = task;
				}else{
					this.toViewTaskFolder = folder;
					this.toViewTask = task;
				}
			} else if (props.isGoogle) {
				this.toEditGoogleEvent = await GoogleCalendarService.getEvent(props.id, props.calendarId);
				this.toEditGoogleEventCalendarId = props.calendarId;
			} else {
				this.toEditEvent = await CalendarService.getEvent(props.id);
			}
		},
		async onEventDrop(e: IOnDropProps) {
			await this.state.saveEvent(e);
		},
		onGotEventType(type: string) {
			if (type == "local") {
				if (this.state.calendars.length == 0) return;
				this.toEditEvent = new CalendarEvent();
				this.toEditEvent.StartTime = this.clickedDate;
				this.toEditEvent.EndTime = new Date(this.clickedDate);
				//this.toEditEvent.EndTime.setHours(this.toEditEvent.EndTime.getHours() + 2);
				this.toEditEvent.setEndTime(this.toEditEvent.EndTime);
				if(this.event?.SaleID){
					this.toEditEvent.Title = this.event.Title;
					this.toEditEvent.SaleID = this.event.SaleID;
					this.toEditEvent.SaleBookYear = this.event.SaleBookYear;
					this.toEditEvent.StartTimeIsLinkedToSaleDeliveryDate = this.event.StartTimeIsLinkedToSaleDeliveryDate;
					this.toEditEvent.GetColorMethod = this.event.GetColorMethod;
				}
			} else {
				let event = GoogleCalendarService.createNewCalendarEventObject();
				event.start.dateTime = this.clickedDate.toISOString();
				let endTime = new Date(this.clickedDate);
				endTime.setHours(this.clickedDate.getHours() + 1);
				event.end.dateTime = endTime.toISOString();
				this.toEditGoogleEvent = event;
			}
		},
		onTouchStart(event: TouchEvent) {
			this.dragInited = true;
			let touch = event.touches.item(0) as Touch;
			this.dragBeginPosition = {
				x: touch.pageX,
				y: touch.pageY
			};
		},
		onMouseDown(event: MouseEvent) {
			this.dragInited = true;
			this.dragBeginPosition = {
				x: event.pageX,
				y: event.pageY
			};
		},
		onTouchMove(event: TouchEvent) {
			if (this.dragInited) {
				this.isDragging = true;
			}
		},
		onMouseMove(event: MouseEvent) {
			if (this.dragInited) {
				this.isDragging = true;
			}
		},
		onTouchEnd(event: TouchEvent) {
			this.dragInited = false;
			if (!this.isDragging) return;
			requestAnimationFrame(() => {
				this.isDragging = false;
				let touch = event.changedTouches.item(0) as Touch;
				let xDiff = this.dragBeginPosition.x - touch.pageX;
				const minDiff = 100;
				if (xDiff < minDiff && xDiff > -minDiff) {
					return;
				}
				if (xDiff > minDiff) {
					this.state.calendarNext();
				}
				if (xDiff < -minDiff) {
					this.state.calendarPrev();
				}
			});
		},
		onMouseUp(event: MouseEvent) {
			this.dragInited = false;
			if (!this.isDragging || this.preventSwipe) return;
			requestAnimationFrame(() => {
				this.isDragging = false;
				let xDiff = this.dragBeginPosition.x - event.pageX;
				const minDiff = 25;
				if (xDiff < minDiff && xDiff > -minDiff) {
					return;
				}

				if (xDiff > minDiff) {
					this.state.calendarNext();
				}
				if (xDiff < -minDiff) {
					this.state.calendarPrev();
				}
			});
		},
		onEventResize(e: { event: EventInput, revert: Function }) {
			this.state.saveEvent(e);
		},
		onEventResizeStart() {
			this.preventSwipe = true;
		},
		onEventResizeStop() {
			this.preventSwipe = false;
		},
		onEventDragStart() {
			this.preventSwipe = true;
		},
		onEventDragStop(event:any) {
			this.preventSwipe = false;
		},
		onResize() {
			let result = (this.$refs.calContainer as Vue).$el.getBoundingClientRect().height;
			this.state.setHeight(result);
		},
		closeTaskView(){
			this.toViewTask = null;
			this.toViewTaskFolder = null;
		},
		async openEventFromRoute() {
			let eventId = parseInt(`${this.$route.query.event || ""}`);
			if (!eventId) return;
			try {
				this.toEditEvent = await CalendarService.getEvent(eventId);
			} catch (err) {
				return;
			}
			this.$router.replace({path: this.$route.path}).catch(() => {});
		},
		editEvent(event:CalendarEvent){
			this.toEditEvent = event;
			if(this.event?.SaleID){
				this.toEditEvent.Title = this.event.Title;
				this.toEditEvent.SaleID = this.event.SaleID;
				this.toEditEvent.SaleBookYear = this.event.SaleBookYear;
				this.toEditEvent.StartTimeIsLinkedToSaleDeliveryDate = this.event.StartTimeIsLinkedToSaleDeliveryDate;
				this.toEditEvent.GetColorMethod = this.event.GetColorMethod;
			}
		}
	},
	async created() {
		this.openEventFromRoute();
		window.addEventListener("touchmove", this.onTouchMove);
		window.addEventListener("touchend", this.onTouchEnd);
		window.addEventListener("touchcancel", this.onTouchEnd);
		window.addEventListener("mousemove", this.onMouseMove);
		window.addEventListener("mouseup", this.onMouseUp);
		window.addEventListener("resize", this.onResize);
	},
	destroyed() {
		window.removeEventListener("touchmove", this.onTouchMove);
		window.removeEventListener("touchend", this.onTouchEnd);
		window.removeEventListener("touchcancel", this.onTouchEnd);
		window.removeEventListener("mousemove", this.onMouseMove);
		window.removeEventListener("mouseup", this.onMouseUp);
		window.removeEventListener("resize", this.onResize);
	},
	components: {
		AppFullCalendar,
		AppEventTypeSelectModal,
		AppCalendarEventEdit,
		AppGoogleCalendarEventEdit,
		AppTaskEdit,
		AppTaskView,
		AppCalendarControls,
		AppCalendarDayPerCalendarView
	},
	i18nEx: {
		componentPrefix: "components.calendar"
	}
});
